import Box from "@mui/material/Box";
import type {PaperProps} from "@mui/material/Paper";
import type {ElementType, ReactNode, Ref} from "react";

import {projectCardClasses as classes} from "./classes.js";
import {ProjectCardMetricRow} from "./ProjectCardParts.js";
import {ProjectCardRoot} from "./ProjectCardRoot.js";

export interface ProjectCardProps extends Omit<PaperProps, "children" | "title"> {
    component?: ElementType;
    /** Clamp name/description so every card renders at the same height. */
    uniformHeight?: boolean;
    /** Switch layouts off the card's own width (container query) instead of the viewport. */
    widthResponsive?: boolean;
    /** Selection checkbox, absolutely positioned in the card's corner. */
    checkbox?: ReactNode;
    /**
     * The thumbnail area, typically a `ProjectCardThumbnailArea` wrapping the
     * host app's linked thumbnail. Omit it entirely for the compact layout.
     */
    thumbnailArea?: ReactNode;
    /** The linked project name, typically a `ProjectCardTitle`. */
    title: ReactNode;
    /** The project description, typically a `ProjectCardDescription`. */
    description?: ReactNode;
    /**
     * Contents of the author row: owner avatar + handle, contributor avatars,
     * and timestamp.
     */
    authorRow?: ReactNode;
    /** Controls that remain visible after the author content is clamped. */
    authorRowTrailing?: ReactNode;
    /** Contents of the metric row: star control and metric counters. */
    metrics?: ReactNode;
    /** Clamp the metric row to a single line (overflowing metrics hidden). */
    metricsCollapsed?: boolean;
    metricsInnerRef?: Ref<HTMLDivElement>;
    /** Rendered outside the clamped metric row, e.g. a show-more button. */
    metricsTrailing?: ReactNode;
    /** Extra nodes rendered inside the card root, e.g. dialogs and menus. */
    children?: ReactNode;
}

/**
 * Pure presentational project card: the shared shell + layout used by the
 * main app's profile surfaces and the site-search app's search results.
 * All data comes in via props; interactive bits are injected as slots so
 * server-rendered consumers can simply omit them.
 */
export function ProjectCard(props: ProjectCardProps) {
    const {
        uniformHeight,
        widthResponsive,
        checkbox,
        thumbnailArea,
        title,
        description,
        authorRow,
        authorRowTrailing,
        metrics,
        metricsCollapsed,
        metricsInnerRef,
        metricsTrailing,
        children,
        className,
        ...rootProps
    } = props;

    return (
        <ProjectCardRoot
            component="article"
            elevation={0}
            {...rootProps}
            uniformHeight={uniformHeight}
            widthResponsive={widthResponsive}
            className={className ? `${classes.cardWrapper} ${className}` : classes.cardWrapper}
        >
            {checkbox}
            <Box className={classes.cardInner}>
                {thumbnailArea}
                <Box className={classes.content}>
                    <Box className={classes.topSection}>
                        {title}
                        {description ?? (uniformHeight ? <Box className={classes.description} /> : null)}
                        {(authorRow || authorRowTrailing) && (
                            <Box className={classes.authorRow}>
                                {authorRow && <Box className={classes.authorRowInner}>{authorRow}</Box>}
                                {authorRowTrailing && (
                                    <Box className={classes.authorRowTrailing}>{authorRowTrailing}</Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    {metrics && (
                        <ProjectCardMetricRow
                            collapsed={metricsCollapsed}
                            innerRef={metricsInnerRef}
                            trailing={metricsTrailing}
                        >
                            {metrics}
                        </ProjectCardMetricRow>
                    )}
                </Box>
            </Box>
            {children}
        </ProjectCardRoot>
    );
}
